import { User } from "@supabase/supabase-js";
import { QueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import { uploadObituaryImageToS3 } from "@/shared/lib/obituaryImageUpload";
import { stripPhone } from "@/shared/utils/formatters";
import { normalizeName, buildFullName } from "@/shared/utils/names";
import { ONBOARDING_STORAGE_KEYS } from "@/shared/constants/constants";
import { Database } from "@/shared/types/database.types";

type UserRow = Database["public"]["Tables"]["users"]["Row"];
type EstateRow = Database["public"]["Tables"]["estates"]["Row"];

export interface OnboardingData {
  userInfo: {
    email: string;
    firstName: string;
    middleName?: string;
    lastName: string;
    phoneNumber: string;
  };
  personInfo: {
    fullName: string;
    dateOfBirth?: string;
    dateOfDeath?: string;
    lastResidence?: string;
    funeralHome?: string;
    mainImageUrl?: string;
    personType: "deceased" | "living";
    obituaryLink?: string;
  };
}

export interface OnboardingResult {
  user: UserRow | null;
  person: EstateRow | null;
  success: boolean;
  error?: string;
}

/**
 * Complete onboarding flow that creates user, person, and permissions in the correct order.
 * This ensures RLS policies work correctly by creating user record before person.
 */
export async function completeOnboarding(
  authUser: User,
  onboardingData: OnboardingData,
  queryClient?: QueryClient,
): Promise<OnboardingResult> {
  try {
    const userRecord = await createUserRecord(authUser, onboardingData.userInfo);
    if (!userRecord) {
      throw new Error("Failed to create user record");
    }

    // Validate phone number asynchronously (non-blocking)
    validatePhoneNumberAsync(userRecord.user_id, onboardingData.userInfo.phoneNumber).catch(
      (error) => console.warn("Phone validation failed (non-blocking):", error),
    );

    // Auto-promote @elayne.com users to admin (non-blocking)
    promoteAdminIfElayne(userRecord.user_id, onboardingData.userInfo.email).catch((error) =>
      console.warn("Admin promotion failed (non-blocking):", error),
    );

    setAcquisitionSource(onboardingData.userInfo.email, "nok").catch((error) =>
      console.warn("Acquisition source tracking failed (non-blocking):", error),
    );

    const personRecord = await createPersonRecord(userRecord.user_id, onboardingData.personInfo);
    if (!personRecord) {
      throw new Error("Failed to create person record");
    }

    await createUserPersonRelationship(userRecord.user_id, personRecord.estate_id);
    await seedEstateTasks(personRecord.estate_id);
    await updateUserLastActivePerson(userRecord.user_id, personRecord.estate_id);

    if (onboardingData.personInfo.mainImageUrl) {
      await handleImageUpload(onboardingData.personInfo.mainImageUrl, personRecord.estate_id);
    }

    if (queryClient) {
      const { refetchCriticalQueries } = await import("@/shared/utils/queryRefetchHelper");
      await refetchCriticalQueries(queryClient);
    }

    return {
      user: userRecord,
      person: personRecord,
      success: true,
    };
  } catch (error) {
    console.error("Onboarding flow failed:", error);
    return {
      user: null,
      person: null,
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}

async function createUserRecord(
  authUser: User,
  userInfo: OnboardingData["userInfo"],
): Promise<UserRow | null> {
  const firstName = normalizeName(userInfo.firstName);
  const middleName = userInfo.middleName ? normalizeName(userInfo.middleName) : null;
  const lastName = normalizeName(userInfo.lastName);

  const userData = {
    auth_id: authUser.id,
    email: userInfo.email,
    first_name: firstName,
    middle_name: middleName,
    last_name: lastName,
    full_name: buildFullName(firstName, middleName ?? "", lastName),
    phone_number: stripPhone(userInfo.phoneNumber),
    completed_onboarding: false,
    completed_authorization: false,
    contact_by_email: true,
    contact_by_phone: false,
  };

  const { data, error } = await supabase.from("users").insert(userData).select().single();

  if (error) {
    console.error("Failed to create user record:", error);
    throw error;
  }

  return data;
}

async function createPersonRecord(
  userId: number,
  personInfo: OnboardingData["personInfo"],
): Promise<EstateRow | null> {
  const fullName = normalizeName(personInfo.fullName);

  const personData = {
    owner_user_id: userId,
    full_name: fullName,
    first_name: fullName.split(" ")[0] || "",
    last_name: fullName.split(" ").slice(1).join(" ") || "",
    date_of_birth: personInfo.dateOfBirth || null,
    date_of_death: personInfo.dateOfDeath || null,
    location: personInfo.lastResidence || null,
    funeral_home: personInfo.funeralHome || null,
    main_image_url: null,
    estate_type: personInfo.personType,
    obituary_link: personInfo.obituaryLink || null,
  };

  const { data, error } = await supabase.from("estates").insert(personData).select().single();

  if (error) {
    console.error("Failed to create person record:", error);
    throw error;
  }

  return data;
}

async function createUserPersonRelationship(userId: number, estateId: number): Promise<void> {
  const { error } = await supabase.from("user_estates").insert({
    user_id: userId,
    estate_id: estateId,
  });

  if (error) {
    console.error("Failed to create user-person relationship:", error);
    throw error;
  }
}

async function updateUserLastActivePerson(userId: number, estateId: number): Promise<void> {
  const { error } = await supabase
    .from("users")
    .update({
      last_active_estate_id: estateId,
      completed_onboarding: false,
    })
    .eq("user_id", userId);

  if (error) {
    console.error("Failed to update user last active person:", error);
    throw error;
  }
}

async function seedEstateTasks(estateId: number): Promise<void> {
  try {
    const { data: seeds, error: seedError } = await supabase.from("seed_tasks").select("task_id");

    if (seedError || !seeds || seeds.length === 0) {
      console.warn("No seed tasks found to assign to person");
      return;
    }

    const rows = seeds.map((s) => ({
      estate_id: estateId,
      task_id: s.task_id,
      status: "not_started",
    }));

    const { error: insertError } = await supabase.from("estate_tasks").insert(rows);

    if (insertError) {
      console.error("Failed to create estate tasks:", insertError);
      throw insertError;
    }
  } catch (error) {
    console.error("Error seeding estate tasks:", error);
  }
}

export async function promoteAdminIfElayne(userId: number, email: string): Promise<void> {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail.endsWith("@elayne.com")) return;

  const response = await supabase.functions.invoke("account/promote-admin", {
    body: { userId, email: normalizedEmail },
  });

  if (response.error) {
    console.error("Admin promotion edge function error:", response.error);
  }
}

async function handleImageUpload(imageUrl: string, estateId: number): Promise<void> {
  try {
    const s3Key = await uploadObituaryImageToS3(imageUrl, estateId);

    if (!s3Key) {
      console.warn("Failed to upload obituary image to S3");
      return;
    }

    const { error } = await supabase
      .from("estates")
      .update({ main_image_url: s3Key })
      .eq("estate_id", estateId);

    if (error) {
      console.error("Failed to update person with image URL:", error);
    }
  } catch (error) {
    console.error("Image upload failed:", error);
  }
}

async function validatePhoneNumberAsync(userId: number, phoneNumber: string): Promise<void> {
  try {
    const response = await supabase.functions.invoke("communications/phone/validate", {
      body: { userId, phoneNumber },
    });

    if (response.error) {
      console.error("Phone validation edge function error:", response.error);
      return;
    }

    if (!response.data?.success) {
      console.warn("Phone validation returned unsuccessful:", response.data);
    }
  } catch (error) {
    console.error("Phone validation async call failed:", error);
  }
}

export function storeOnboardingData(data: Partial<OnboardingData>): void {
  const existingData = getStoredOnboardingData();
  const updatedData = { ...existingData, ...data };
  localStorage.setItem(ONBOARDING_STORAGE_KEYS.DATA, JSON.stringify(updatedData));
}

export function getStoredOnboardingData(): Partial<OnboardingData> {
  try {
    const data = localStorage.getItem(ONBOARDING_STORAGE_KEYS.DATA);
    return data ? JSON.parse(data) : {};
  } catch {
    return {};
  }
}

/**
 * Mark onboarding as fully completed (after user makes pricing choice).
 * Also invalidates relevant cache keys to ensure dashboard loads fresh data.
 */
export async function markOnboardingCompleted(
  userId: number,
  queryClient?: QueryClient,
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from("users")
      .update({ completed_onboarding: true })
      .eq("user_id", userId);

    if (error) {
      console.error("Failed to mark onboarding as completed:", error);
      return false;
    }

    if (queryClient) {
      await queryClient.invalidateQueries({
        queryKey: ["app-user-data"],
        exact: false,
      });
    }

    return true;
  } catch (error) {
    console.error("Error marking onboarding as completed:", error);
    return false;
  }
}

export function clearOnboardingData(): void {
  for (const key of Object.values(ONBOARDING_STORAGE_KEYS)) {
    localStorage.removeItem(key);
  }
}

export async function setAcquisitionSource(
  email: string,
  userType: "nok" | "lawyer",
): Promise<void> {
  const response = await supabase.functions.invoke("account/set-acquisition-source", {
    body: { email, userType },
  });

  if (response.error) {
    console.error("Acquisition source edge function error:", response.error);
  }
}
